import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setIsFullscreen } from '../../store/slices/editorUISlice';
import WaveForm from '../../../../components/MediaContainer/WaveForm';
import SeekingBar from '../SeekingBar/index';
import snapsopLogo from '../../../../assets/images/snapsop-transparent.svg';

const TIMELINE_HEIGHT = 250; // matches CreateMeetingFooter height
const TIMELINE_GAP = 4; // margin between preview container and footer timeline wrapper
const MIN_PREVIEW_HEIGHT = 600;
const VIEWER_MIN_PREVIEW_HEIGHT = 680;

const getCssZoom = () => {
  const zoomValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--zoom')
    .trim();
  return parseFloat(zoomValue) || 1;
};

const computePreviewHeight = (element, minHeight) => {
  if (!element) return minHeight;

  const zoom = getCssZoom();
  const viewportHeight = window.innerHeight / zoom;
  const topOffset = element.getBoundingClientRect().top / zoom;
  const available =
    viewportHeight - topOffset - TIMELINE_HEIGHT - TIMELINE_GAP;

  return Math.max(minHeight, available);
};

export default function PreviewContainer({
  videoRef,
  isAudioEnabled,
  isRecording,
  isTimerRunning,
  isCameraEnabled,
  isScreenEnabled,
  streamRef,
  timerValue,
  recordingTime,
  isCameraRecordingPaused,
  hasMultipleCameras,
  renderPreview,
  startCountdownThenStart,
  handlePauseRecording,
  handleResumeRecording,
  handleStopRecording,
  cancelArming,
  handleSwitchCamera,
  currentItem,
  currentAsset,
  isViewerMode = false,
  containerStyle = null,
}) {
  const dispatch = useDispatch();
  // Preview container ref - moved from parent
  const previewContainerRef = useRef();
  // Fullscreen container ref
  const fullscreenContainerRef = useRef();
  const [previewHeight, setPreviewHeight] = useState(null);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      dispatch(setIsFullscreen(!!document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [dispatch]);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!fullscreenContainerRef.current) return;
      setPreviewHeight(
        computePreviewHeight(
          fullscreenContainerRef.current,
          isViewerMode ? VIEWER_MIN_PREVIEW_HEIGHT : MIN_PREVIEW_HEIGHT,
        ),
      );
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const containerHeightStyle =
    previewHeight !== null
      ? {
          height: `${previewHeight}px`,
          flex: '0 0 auto',
        }
      : {
        // Fallback to legacy constant until we can measure
          height: 'calc(100vh - 320px)',
          flex: '0 0 auto',
        };

  return (
    <div
      ref={fullscreenContainerRef}
      style={{
        ...containerHeightStyle,
        minHeight: isViewerMode ? VIEWER_MIN_PREVIEW_HEIGHT : MIN_PREVIEW_HEIGHT,
        width: '100%',
        maxWidth: '100%',
        background: '#0b0b0b',
        backgroundImage: `url(${snapsopLogo})`,
        backgroundRepeat: 'repeat',
        backgroundSize: '140px auto',
        backgroundPosition: '0 0',
        borderRadius: 12,
        border: '1px solid #1f2937',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#d1d5db',
        position: 'relative',
        overflow: 'hidden',
        ...(containerStyle || {}),
      }}
    >
      <div
        ref={previewContainerRef}
        style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 }}
      >
        {/* Show live recording preview when capturing */}
        {(() => {
          if (isAudioEnabled) {
            return (
              <WaveForm
                containerRef={previewContainerRef}
                isRecording={isRecording}
                mediaStream={streamRef.current}
                isAudioEnabled={isAudioEnabled}
                fullHeight
              />
            );
          }
          if (isRecording || isTimerRunning || isCameraEnabled || isScreenEnabled) {
            return (
              <video
                ref={videoRef}
                muted
                autoPlay
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
              />
            );
          }
          return renderPreview();
        })()}
      </div>

      {/* Countdown overlay */}
      {isTimerRunning && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
            cursor: 'default',
          }}
        >
          <div
            style={{
              width: 140,
              height: 140,
              borderRadius: '9999px',
              background: '#111827',
              border: '2px solid #374151',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#F3F4F6',
              fontSize: 56,
              fontWeight: 700,
            }}
          >
            {timerValue}
          </div>
        </div>
      )}

      {/* REC badge + timer */}
      {isRecording && !isCameraRecordingPaused && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 16,
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            zIndex: 15,
            color: '#F3F4F6',
            fontWeight: 600,
          }}
        >
          <div style={{ width: 12, height: 12, borderRadius: 12, background: '#EF4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.5)' }} />
          <span>REC</span>
          <span style={{ marginLeft: 8 }}>
            {new Date(recordingTime * 1000).toISOString().substring(11, 19)}
          </span>
        </div>
      )}

      {/* YouTube-style seeking bar */}
      <SeekingBar
        onStartRecording={startCountdownThenStart}
        onPauseRecording={handlePauseRecording}
        onResumeRecording={handleResumeRecording}
        onStopRecording={handleStopRecording}
        onCancelRecording={cancelArming}
        onSwitchCamera={handleSwitchCamera}
        hasMultipleCameras={hasMultipleCameras}
        currentItem={currentItem}
        currentAsset={currentAsset}
        fullscreenContainerRef={fullscreenContainerRef}
      />

      {/* Overlay to reduce pattern opacity */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(11, 11, 11, 0.90)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
    </div>
  );
}
